<script setup lang="ts">
import { computed, onMounted, type PropType, ref, watch } from "vue";
import {
  BgImage,
  ControlSize,
  CssSize,
  DesignVersion,
  HeightSize,
  ModalPosition,
  WidthSize,
} from "@/models/types";
import BcButton from "./bc-button.vue";

defineOptions({ name: "BcModal" });

/** Specifies two-way binding (for visibility) for the modal */
const modelValue = defineModel<boolean>({ default: true });

const props = defineProps({
  /** Changes modal to alert dialog */
  alertDialog: { type: Boolean, default: false },
  /** Sets the button text for the alert dialog */
  alertOkText: { type: String, default: "OK" },
  /** Specifies animation for the modal */
  animation: { type: String, default: "zoom-out" },
  /** Specifies background visibility for the modal */
  background: { type: Boolean, default: true },
  /** Includes a backdrop layer to the background */
  backdrop: { type: Boolean, default: true },
  /** Specifies background image for the modal */
  backgroundImage: {
    type: String as PropType<BgImage>,
    validator: (value: any) => BgImage.isBgImage(value),
  },
  /** Sets a border around the modal */
  bordered: { type: Boolean, default: true },
  /** Adds close icon to the header */
  closeIcon: { type: Boolean, default: false },
  /** Changes modal to confirm dialog */
  confirmDialog: { type: Boolean, default: false },
  /** Specifies the modal's style */
  design: {
    type: String as PropType<DesignVersion>,
    validator: (value: any) => DesignVersion.isDesignVersion(value),
  },
  /** Sets a divider for the footer */
  divideFooter: { type: Boolean, default: false },
  /** Specifies the close behavior */
  closeOnClick: { type: Boolean, default: true },
  /** Sets the `No` button text for the confirm dialog */
  confirmNoText: { type: String, default: "Cancel" },
  /** Sets the `Yes` button text for the confirm dialog */
  confirmYesText: { type: String, default: "OK" },
  /** Sets the dialog title added to the header */
  dialogTitle: { type: String },
  /** Sets a divider for the header */
  divideHeader: { type: Boolean, default: false },
  /** Makes the modal dialog full screen */
  fullScreen: { type: Boolean, default: false },
  /** Specifies the modal's height */
  height: {
    type: [String, Number],
    validator: (value: any) => {
      if (typeof value !== "string" && typeof value !== "number") return false;
      if (Number.isNaN(Number.parseFloat(value.toString()))) return false;
      const unit = value
        .toString()
        .replace(/\s/g, "")
        .replace(Number.parseFloat(value.toString()).toString(), "");
      return (
        typeof value === "number" ||
        CssSize.isCssSize(unit) ||
        HeightSize.isHeightSize(unit)
      );
    },
  },
  /** Specifies inner background visibility for the modal */
  innerBackground: { type: Boolean, default: false },
  /** Specifies the modal's minimum height */
  minHeight: {
    type: [String, Number],
    validator: (value: any) => {
      if (typeof value !== "string" && typeof value !== "number") return false;
      if (Number.isNaN(Number.parseFloat(value.toString()))) return false;
      const unit = value
        .toString()
        .replace(/\s/g, "")
        .replace(Number.parseFloat(value.toString()).toString(), "");
      return (
        typeof value === "number" ||
        CssSize.isCssSize(unit) ||
        HeightSize.isHeightSize(unit)
      );
    },
  },
  /** Specifies the modal's minimum width */
  minWidth: {
    type: [String, Number],
    validator: (value: any) => {
      if (typeof value !== "string" && typeof value !== "number") return false;
      if (Number.isNaN(Number.parseFloat(value.toString()))) return false;
      const unit = value
        .toString()
        .replace(/\s/g, "")
        .replace(Number.parseFloat(value.toString()).toString(), "");
      return (
        typeof value === "number" ||
        CssSize.isCssSize(unit) ||
        WidthSize.isWidthSize(unit)
      );
    },
  },
  /** Sets the modal position */
  modalPosition: {
    type: String as PropType<ModalPosition>,
    default: "middle-center",
    validator: (value: any) => ModalPosition.isModalPosition(value),
  },
  /** Makes modal border rounded */
  rounded: { type: Boolean, default: false },
  /** Specifies pre-defined sizes for the modal */
  size: {
    type: String as PropType<ControlSize>,
    validator: (value: any) => ControlSize.isControlSize(value),
  },
  /** Specifies the modal's width */
  width: {
    type: [String, Number],
    validator: (value: any) => {
      if (typeof value !== "string" && typeof value !== "number") return false;
      if (Number.isNaN(Number.parseFloat(value.toString()))) return false;
      const unit = value
        .toString()
        .replace(/\s/g, "")
        .replace(Number.parseFloat(value.toString()).toString(), "");
      return (
        typeof value === "number" ||
        CssSize.isCssSize(unit) ||
        WidthSize.isWidthSize(unit)
      );
    },
  },
});

const emit = defineEmits<{
  (e: "alert-ok"): void;
  (e: "confirm-no"): void;
  (e: "confirm-yes"): void;
  (e: "update:active", payload: boolean): void;
  (e: "update:model-value", payload: boolean): void;
}>();

const dialogRef = ref<HTMLDivElement | null>(null);
const dialogBodyRef = ref<HTMLElement | null>(null);
const modalControlRef = ref<HTMLDivElement | null>(null);

const pak = computed<string>(() => {
  return "bc-controls-d" + props.design;
});

/** Specifies two-way binding (for visibility) for the modal */
const active = computed({
  get: () => modelValue.value,
  set: (value) => emit("update:model-value", value),
});
const positionClasses = computed(() => {
  return props.modalPosition.split("-");
});

function alertOkClicked(): void {
  emit("alert-ok");
  modelValue.value = false;
  emit("update:active", false);
}

function confirmYesClicked(): void {
  emit("confirm-yes");
  modelValue.value = false;
  emit("update:active", false);
}

function confirmNoClicked(): void {
  emit("confirm-no");
  modelValue.value = false;
  emit("update:active", false);
}

function closeIconClicked(): void {
  modelValue.value = false;
  emit("update:active", false);
}

function modalClicked(event: MouseEvent): void {
  if (
    modelValue.value &&
    props.closeOnClick &&
    dialogRef.value &&
    event.target !== dialogRef.value &&
    !dialogRef.value.contains(event.target as Node)
  ) {
    modelValue.value = false;
    emit("update:active", false);
  }
}

function setDialogHeight() {
  props.height !== undefined && props.height !== null
    ? modalControlRef.value?.style.setProperty(
        "--modal_dialog_height",
        typeof props.height === "number"
          ? `${props.height}px`
          : props.height.replace(/\s/g, "")
      )
    : modalControlRef.value?.style.setProperty("--modal_dialog_height", "unset");
}

function setDialogWidth() {
  props.width !== undefined && props.width !== null
    ? modalControlRef.value?.style.setProperty(
        "--modal_dialog_width",
        typeof props.width === "number"
          ? `${props.width}px`
          : props.width.replace(/\s/g, "")
      )
    : modalControlRef.value?.style.setProperty("--modal_dialog_width", "unset");
}

function setDialogMinHeight() {
  props.minHeight !== undefined && props.minHeight !== null
    ? dialogRef.value?.style.setProperty(
        "min-height",
        typeof props.minHeight === "number"
          ? `${props.minHeight}px`
          : props.minHeight.replace(/\s/g, "")
      )
    : dialogRef.value?.style.removeProperty("min-height");
}

function setDialogMinWidth() {
  props.minWidth !== undefined && props.minWidth !== null
    ? dialogRef.value?.style.setProperty(
        "min-width",
        typeof props.minWidth === "number"
          ? `${props.minWidth}px`
          : props.minWidth.replace(/\s/g, "")
      )
    : dialogRef.value?.style.removeProperty("min-width");
}

onMounted(() => {
  // Set size
  setDialogWidth();
  setDialogHeight();
  setDialogMinWidth();
  setDialogMinHeight();
});

watch(() => props.width, setDialogWidth);
watch(() => props.height, setDialogHeight);
watch(() => props.minWidth, setDialogMinWidth);
watch(() => props.minHeight, setDialogMinHeight);

defineExpose({
  modelValue,
  active,
  dialogRef,
  dialogBodyRef,
  modalControlRef,
});
</script>

<template>
  <transition :name="animation">
    <div
      ref="modalControlRef"
      :data-pak="pak"
      class="bissantzControls modalControl"
      :class="[
        positionClasses,
        {
          active: modelValue,
          allowBackground: background,
        },
      ]"
      :data-design="design"
      :data-background-image="backgroundImage"
      v-show="modelValue"
      @click="modalClicked"
    >
      <div class="background" :class="{ backdrop: backdrop }">
        <div v-if="innerBackground" class="innerBackground"></div>
      </div>

      <div
        ref="dialogRef"
        class="dialog"
        :class="[
          size ? `dialog-${size}` : '',
          {
            bordered: bordered,
            fullScreen: fullScreen,
            rounded: rounded,
          },
        ]"
      >
        <header
          v-if="$slots.header || dialogTitle"
          class="dialogHeader"
          :class="{ divide: divideHeader }"
        >
          <slot name="header"
            ><h5 class="dialogTitle">{{ dialogTitle }}</h5></slot
          >
          <bc-button v-if="closeIcon" :design="design" @click="closeIconClicked" plain
            >🞪</bc-button
          >
        </header>
        <main
          v-if="$slots.main || $slots.body || $slots.default"
          ref="dialogBodyRef"
          class="dialogBody"
        >
          <slot v-if="$slots.main" name="main" />
          <slot v-else-if="$slots.body" name="body" />
          <slot v-else-if="$slots.default" />
        </main>
        <footer
          v-if="$slots.footer || alertDialog || confirmDialog"
          class="dialogFooter"
          :class="{ divide: divideFooter }"
        >
          <template v-if="alertDialog">
            <bc-button :design="design" size="md" @click="alertOkClicked">{{
              alertOkText
            }}</bc-button>
          </template>
          <template v-else-if="confirmDialog">
            <bc-button :design="design" size="md" @click="confirmYesClicked">{{
              confirmYesText
            }}</bc-button>
            <bc-button :design="design" size="md" @click="confirmNoClicked">{{
              confirmNoText
            }}</bc-button>
          </template>
          <slot v-else name="footer" />
        </footer>
      </div>
    </div>
  </transition>
</template>

<style lang="less">
@import "../styles/components/bc-modal.less";
</style>
